import { Dispatch, SetStateAction } from 'react';

import { AspectRatio, ImageForDropzone, ImageInput } from 'app/shared/typings';

interface OriginalFileInfo {
  width: number;
  height: number;
  url: string;
}

const getCropJSONForOriginalFile = (originalFileInfo: OriginalFileInfo) => {
  return {
    inputPath: originalFileInfo.url,
    pipeline: {
      steps: [
        {
          geometry: {
            offset: { x: 0, y: 0 },
            size: {
              width: originalFileInfo.width,
              height: originalFileInfo.height,
              type: 'widthxheight!',
            },
          },
          type: 'crop',
        },
      ],
    },
  };
};

async function fetchUploadIOCropJSON(
  originalFileInfo: OriginalFileInfo,
  cropFileLocation?: string
) {
  if (!cropFileLocation) {
    return getCropJSONForOriginalFile(originalFileInfo);
  }

  try {
    const response = await fetch(cropFileLocation);
    return await response.json();
  } catch (error) {
    console.error(error);
  }
}

async function fetchImageDimensions(fileLocation: string) {
  const img = new Image();
  img.src = fileLocation;
  await img.decode();
  return [img.naturalWidth, img.naturalHeight];
}

function translateUploadIOToImgIXParams({
  crop_horizontal_offset,
  crop_vertical_offset,
  crop_width,
  crop_height,
  originalFileWidth,
  originalFileHeight,
  ratio,
}: {
  crop_horizontal_offset: number;
  crop_vertical_offset: number;
  crop_width: number;
  crop_height: number;
  originalFileWidth: number;
  originalFileHeight: number;
  ratio: AspectRatio;
}) {
  const originalImageRatio = originalFileWidth / originalFileHeight;
  const cropImageRatio = crop_width / crop_height;
  const fp_x = (
    (crop_horizontal_offset + crop_width / 2) /
    originalFileWidth
  ).toPrecision(4);
  const fp_y = (
    (crop_vertical_offset + crop_height / 2) /
    originalFileHeight
  ).toPrecision(4);
  const fp_z = (
    cropImageRatio < originalImageRatio
      ? originalFileHeight / crop_height
      : originalFileWidth / crop_width
  ).toPrecision(4);

  return `?fit=crop&crop=focalpoint&w=${crop_width}&h=${crop_height}&fp-x=${fp_x}&fp-y=${fp_y}&fp-z=${fp_z}&ar=${ratio}`;
}

export async function calculateImgIXParams({
  setImgIXParamsMap,
  imagesForAllRatios,
}: {
  setImgIXParamsMap: Dispatch<SetStateAction<Record<string, string>>>;
  imagesForAllRatios: ImageForDropzone[];
}) {
  imagesForAllRatios.map(async (image) => {
    const [originalFileWidth, originalFileHeight] = await fetchImageDimensions(
      image.originalFileUrl || ''
    );
    const originalFileInfo = {
      width: originalFileWidth,
      height: originalFileHeight,
      url: image.originalFileUrl,
    };

    const uploadIOCropJSON = await fetchUploadIOCropJSON(
      originalFileInfo,
      image.editedFileUrl
    );

    if (uploadIOCropJSON && originalFileWidth && originalFileHeight) {
      const cropInstructions = uploadIOCropJSON.pipeline.steps;
      const {
        offset: { x: crop_horizontal_offset, y: crop_vertical_offset },
        size: { width: crop_width, height: crop_height },
      } = cropInstructions[0].geometry;

      const imgIXParams = translateUploadIOToImgIXParams({
        crop_horizontal_offset,
        crop_vertical_offset,
        crop_width,
        crop_height,
        originalFileWidth,
        originalFileHeight,
        ratio: image.ratio,
      });

      setImgIXParamsMap((currentImgIXParamsMap) => {
        return {
          ...currentImgIXParamsMap,
          [image.fileUrl]: imgIXParams,
        };
      });
    }
  });
}

interface Props {
  imagesForAllRatios: ImageForDropzone[];
  imgIXParamsMap: Record<string, string>;
}

export function getImagesToSave({
  imagesForAllRatios,
  imgIXParamsMap,
}: Props): ImageInput[] {
  return imagesForAllRatios.map((image) => {
    // originalFileName = e.g. City-29-1.3333333333333333-1700176971453-image.jpg
    const originalFileName = image.originalFileUrl.split('/').pop();
    const filePlatform = 'imgix';
    const imgIXParams = imgIXParamsMap[image.fileUrl] || '';
    const filename = originalFileName + imgIXParams;

    return {
      contentType: image.mime,
      filePlatform,
      filename,
      ratio: image.ratio,
      size: image.size,
      title: image.title,
      description: image.description,
    };
  });
}

const COMPRESSIBLE_MIME_TYPES = ['image/jpeg', 'image/png'];

interface CompressImageFileOptions {
  // Files at or below this size are returned untouched.
  skipBelowBytes?: number;
  // Longest edge (px) to downscale to.
  maxDimension?: number;
  // JPEG quality (0-1). Ignored for PNGs (lossless format).
  quality?: number;
}

const DEFAULT_COMPRESS_OPTIONS: Required<CompressImageFileOptions> = {
  skipBelowBytes: 1.5 * 1024 * 1024,
  maxDimension: 2000,
  quality: 0.8,
};

const isCompressibleImage = (file: File) =>
  COMPRESSIBLE_MIME_TYPES.includes(file.type);

const loadImageElement = async (file: File) => {
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve(img);
      };
      img.onerror = () => {
        reject(new Error('Failed to load image for compression.'));
      };
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const drawAndEncode = (
  img: HTMLImageElement,
  maxDimension: number,
  quality: number,
  mimeType: string
): Promise<Blob | null> => {
  const scale = Math.min(
    1,
    maxDimension / Math.max(img.naturalWidth, img.naturalHeight)
  );
  const targetWidth = Math.round(img.naturalWidth * scale);
  const targetHeight = Math.round(img.naturalHeight * scale);

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext('2d');
  if (!context) {
    return Promise.resolve(null);
  }
  context.drawImage(img, 0, 0, targetWidth, targetHeight);

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
};

// Downscales/re-encodes an image client-side before upload, so large photos upload faster.
// Falls back to the original file on any error, or if it's already small enough to skip.
export const compressImageFile = async (
  file: File,
  options: CompressImageFileOptions = {}
): Promise<File> => {
  const { skipBelowBytes, maxDimension, quality } = {
    ...DEFAULT_COMPRESS_OPTIONS,
    ...options,
  };

  if (!isCompressibleImage(file) || file.size <= skipBelowBytes) {
    return file;
  }

  try {
    const img = await loadImageElement(file);
    const compressedBlob = await drawAndEncode(
      img,
      maxDimension,
      quality,
      file.type
    );

    if (!compressedBlob || compressedBlob.size >= file.size) {
      // Compression didn't help (e.g. an already-optimized image) - keep the original.
      return file;
    }

    return new File([compressedBlob], file.name, {
      type: compressedBlob.type || file.type,
      lastModified: file.lastModified,
    });
  } catch (error) {
    // Never block an upload because compression failed - fall back to the original file.
    console.error(
      '[compressImageFile] Failed to compress image, uploading original file.',
      error
    );
    return file;
  }
};
